/* eslint-disable @typescript-eslint/no-explicit-any */
/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Mantine
import {
	ActionIcon,
	Box,
	Button,
	Group,
	Image,
	Drawer as NativeDrawer,
	Text,
} from '@mantine/core';

// Utils
import {
	convertProduction,
	getMintStatusLabel,
	getMintStatusType,
	handleOpenTransactionExternalLink,
	truncateString,
} from '@utils/functions';

// Components
import { MintStatus } from '@components/atoms/MintStatus';
import { Tooltip } from '@components/atoms/Tooltip';
import { VerifiedButton } from '@components/atoms/VerifiedButton';

// Style
import classes from './DrawerProduction.module.css';

// Icons
import ProductionLogo from '@assets/images/production-logo.svg';

// Icons
import { ACLS } from '@config/constants/acls';
import { NOT_APPLICABLE } from '@config/constants/common';
import { ROLES } from '@config/constants/roles';
import { useAuthorization } from '@lib/authorization';
import { useMediaQuery } from '@mantine/hooks';
import {
	IconExternalLink,
	IconPlayerPlay,
	IconTrash,
} from '@tabler/icons-react';
import isNil from 'lodash/isNil';
import { useTranslation } from 'react-i18next';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const DrawerProduction = (props: {
	onClose: () => void;
	handledelete?: (arg: any) => void;
	handlestartmint?: (arg: any) => void;
	handleverify: (arg: any) => void;
	opened: boolean;
	data: any;
	position: any;
	fromstepper?: boolean;
}) => {
	/* ******************************** Media Query ******************************* */
	const isBigScreen = useMediaQuery('(min-width: 120em)');
	const {
		opened,
		onClose,
		handleverify,
		handledelete,
		handlestartmint,
		fromstepper,
		...restProps
	} = props;
	const { t } = useTranslation();
	const { checkRole, checkAcl } = useAuthorization();
	/* ******************************** RENDERING ******************************* */
	if (props.data)
		return (
			<NativeDrawer
				onClose={onClose}
				opened={opened}
				classNames={classes}
				overlayProps={{
					backgroundOpacity: 0.2,
					blur: 0.5,
				}}
				{...restProps}
			>
				{/* Header */}
				<Box className={classes.customHeader}>
					<Box className={classes.containerLogo}>
						<Image
							className={classes.logo}
							src={ProductionLogo}
						/>
					</Box>

					<Box className={classes.containerTitle}>
						<Text className={classes.customSubTitle}>
							{t('global.project')}
						</Text>
						{props.data.project && (
							<Text className={classes.customTitle}>
								{props.data.project}
							</Text>
						)}
					</Box>
				</Box>

				{/* Body */}
				<Box className={classes.bodyDrawer}>
					{!isNil(props.data.organization) &&
						!isNil(props.data.organization.name) && (
							<Box className={classes.row}>
								<Text className={classes.label}>
									{t('global.organization')}
								</Text>
								<Tooltip
									label={props.data.organization.name}
									isActive={
										props.data.organization.name.length >
										20
									}
								>
									<Text className={classes.value}>
										{truncateString(
											props.data.organization.name,
											20
										)}
									</Text>
								</Tooltip>
							</Box>
						)}
					{!isNil(props.data.entity) &&
						!isNil(props.data.entity.name) && (
							<Box className={classes.row}>
								<Text className={classes.label}>
									{t('global.producer')}
								</Text>
								<Tooltip
									label={props.data.entity.name}
									isActive={
										props.data.entity.name.length > 20
									}
								>
									<Text className={classes.value}>
										{truncateString(
											props.data.entity.name,
											20
										)}
									</Text>
								</Tooltip>
							</Box>
						)}

					{props.data.quantity && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.quantity')}
							</Text>
							<Text className={classes.value}>
								{convertProduction(
									props.data.quantity,
									props.data.unit
								)}
							</Text>
						</Box>
					)}
					{props.data.startDate && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.dateStart')}
							</Text>
							<Text className={classes.value}>
								{props.data.startDate}
							</Text>
						</Box>
					)}
					{props.data.endDate && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.dateEnd')}
							</Text>
							<Text className={classes.value}>
								{props.data.endDate}
							</Text>
						</Box>
					)}

					<Box className={classes.row}>
						<Text className={classes.label}>
							{t('global.fcApplicable')}
						</Text>
						<Text className={classes.value}>
							{props.data.fcApplicable}
						</Text>
					</Box>

					{props.data.tokenEquivalent && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.tokenEquivalent')}
							</Text>
							<Text className={classes.value}>
								{props.data.tokenEquivalent}
							</Text>
						</Box>
					)}
					{props.data.verifiedBy &&
						props.data.verifiedBy !== NOT_APPLICABLE && (
							<Box className={classes.row}>
								<Text className={classes.label}>
									{t('global.verifiedBy')}
								</Text>
								<Text className={classes.value}>
									{props.data.verifiedBy}
								</Text>
							</Box>
						)}
					{props.data.project && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.status')}
							</Text>
							<Text className={classes.value}>
								<VerifiedButton
									data-testid="P2DlDXR7_Z5jdHi6B-button"
									readOnly={
										(fromstepper &&
											!checkRole([
												ROLES.ADMIN,
												ROLES.SUPERADMIN,
												ROLES.STAFF,
											])) ||
										!checkAcl(ACLS.PRODUCTIONS.ADMIN)
									}
									onClick={() => {
										if (
											!props.data?.aco2Token?.mint &&
											checkRole([
												ROLES.ADMIN,
												ROLES.SUPERADMIN,
												ROLES.STAFF,
											]) &&
											checkAcl(ACLS.PRODUCTIONS.ADMIN)
										)
											handleverify(props.data.verified);
									}}
									isverified={props.data.verified}
								>
									{props.data.verified
										? t('global.verified')
										: t('global.notVerified')}
								</VerifiedButton>
							</Text>
						</Box>
					)}
					<Box className={classes.row}>
						<Text className={classes.label}>
							{t('global.mint')}
						</Text>
						<MintStatus
							type={getMintStatusType(
								props.data?.mintStatus
							)}
						>
							{props.data?.mintStatus
								? getMintStatusLabel(
										props.data?.mintStatus
										// eslint-disable-next-line no-mixed-spaces-and-tabs
									)
								: t('global.none')}
						</MintStatus>
					</Box>

					{props.data.blockchain !== NOT_APPLICABLE && (
						<Box className={classes.row}>
							<Text className={classes.label}>
								{t('global.blockchain')}
							</Text>
							<Text className={classes.value}>
								{props.data.blockchain
									? props.data.blockchain
									: NOT_APPLICABLE}
							</Text>
						</Box>
					)}

					{props.data.transactionHash !==
						NOT_APPLICABLE && (
						<Box className={classes.row}>
							<Group
								gap={isBigScreen ? 'xs' : '5px'}
								align="center"
								justify="center"
							>
								<Text className={classes.label}>
									{t('global.transactionId')}
								</Text>
								<ActionIcon
									onClick={() =>
										handleOpenTransactionExternalLink(
											props.data.transactionHash
										)
									}
									variant="transparent"
								>
									<IconExternalLink
										className={classes.label}
										size={'15px'}
									/>
								</ActionIcon>
							</Group>
							<Text
								onClick={() =>
									handleOpenTransactionExternalLink(
										props.data.transactionHash
									)
								}
								className={classes.transactionId}
							>
								{truncateString(
									props.data.transactionHash,
									isBigScreen ? 23 : 18
								)}
							</Text>
						</Box>
					)}
				</Box>

				{/* Footer */}
				{!fromstepper &&
					checkRole([
						ROLES.ADMIN,
						ROLES.SUPERADMIN,
						ROLES.STAFF,
					]) &&
					checkAcl(ACLS.PRODUCTIONS.ADMIN) && (
						<Box className={classes.footer}>
							<Button
								disabled={
									props.data?.aco2Token?.mint ||
									!props.data.verified
								}
								onClick={handlestartmint}
								leftSection={<IconPlayerPlay size={16} />}
								hiddenFrom="xxl"
								size="sm"
								fullWidth
								classNames={{
									root: classes.customFilledButton,
								}}
							>
								{t('global.startMint')}
							</Button>
							<Button
								disabled={
									props.data?.aco2Token?.mint ||
									!props.data.verified
								}
								onClick={handlestartmint}
								leftSection={<IconPlayerPlay size={20} />}
								size="md"
								visibleFrom="xxl"
								fullWidth
								classNames={{
									root: classes.customFilledButton,
								}}
							>
								{t('global.startMint')}
							</Button>

							<Box className={classes.footerSpacer} />
							<Button
								disabled={props.data.verified}
								onClick={handledelete}
								leftSection={<IconTrash size={16} />}
								hiddenFrom="xxl"
								size="sm"
								fullWidth
								classNames={{
									root: classes.customDeleteButton,
								}}
							>
								{t('global.deleteProduction')}
							</Button>
							<Button
								disabled={props.data.verified}
								onClick={handledelete}
								leftSection={<IconTrash size={20} />}
								size="md"
								visibleFrom="xxl"
								fullWidth
								classNames={{
									root: classes.customDeleteButton,
								}}
							>
								{t('global.deleteProduction')}
							</Button>
						</Box>
					)}
			</NativeDrawer>
		);
};
